import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Userpilot } from 'userpilot';

import config from 'config/Config';
import {
  SelectComapnyName,
  selectOrganizationLicense,
} from 'state/Organization/organizationSlice';
import {
  isLDTeamManager,
  selectIsFirstUser,
  selectOrganizationId,
  selectUserEmail,
  selectUserFirstLoginDate,
  selectUserId,
  selectUserLastLoginDate,
  selectUserName,
  selectUserRole,
  selectUserType,
} from 'state/User/userSlice';

import { License } from 'utils/customTypes';
import {
  LICENSE_TIER,
  LICENSE_TIER_FOR_USERPILOT_DISPLAY,
  USERPILOT_EVENTS,
} from 'utils/constants';

const useUserpilot = (): boolean => {
  const [isUserpilotInitialized, setIsUserpilotInitialized] =
    useState<boolean>(false);

  const organizationId = useSelector(selectOrganizationId);
  const currentUserName = useSelector(selectUserName);
  const currentUserEmail = useSelector(selectUserEmail);
  const currentUserId = useSelector(selectUserId);
  const userType = useSelector(selectUserType);
  const isFirstUser = useSelector(selectIsFirstUser);
  const licenseData: License = useSelector(selectOrganizationLicense);
  const companyName = useSelector(SelectComapnyName);
  const role = useSelector(selectUserRole);
  const isTeamManager = useSelector(isLDTeamManager);
  const firstLoginDate = useSelector(selectUserFirstLoginDate);
  const lastLoginDate = useSelector(selectUserLastLoginDate);

  const licenseType = useMemo(() => {
    if (licenseData.license_tier === LICENSE_TIER.REGULAR) {
      return LICENSE_TIER_FOR_USERPILOT_DISPLAY.REGULAR;
    } else {
      return licenseData.license_tier || '';
    }
  }, [licenseData.license_tier]);

  // Userpilot only runs in production environments. Non-prod builds (staging
  // and all dev envs) leave it disabled via the `userpilotEnabled` env config
  // flag (default false in base.json); the staging Userpilot tenant is now
  // dedicated to the next-gen app.
  const isUserpilotEnabled: boolean = config.get('userpilotEnabled', false);

  useEffect(() => {
    if (!isUserpilotEnabled) {
      // Nothing to load: report "initialized" so AuthenticatedPage, which
      // gates rendering on this flag, doesn't get stuck on the loader.
      setIsUserpilotInitialized(true);
      return;
    }

    if (
      currentUserEmail &&
      currentUserId &&
      organizationId &&
      companyName &&
      !isUserpilotInitialized
    ) {
      try {
        Userpilot.initialize(process.env.REACT_APP_USERPILOT_TOKEN || '');
        Userpilot.identify(currentUserId, {
          name: currentUserName,
          email: currentUserEmail,
          company: {
            id: organizationId || '',
            name: companyName,
            lastLoginDate: lastLoginDate || '',
          },
          organizationName: companyName,
          organizationId: organizationId || '',
          firstLoginDate: firstLoginDate || '',
          lastLoginDate: lastLoginDate || '',
          organizationLicense: licenseType || '',
          userType: userType || '',
          role: role || '',
          isTeamManager: isTeamManager || false,
        });
        Userpilot.track(USERPILOT_EVENTS.USER_LOGGED_IN, {
          email: currentUserEmail,
          firstUser: isFirstUser,
          companyId: organizationId,
        });
      } catch (error) {
        //eslint-disable-next-line no-console
        console.error('Error initializing Userpilot', error);
      } finally {
        setIsUserpilotInitialized(true);
      }
    }
  }, [
    isUserpilotEnabled,
    currentUserEmail,
    currentUserId,
    organizationId,
    currentUserName,
    isFirstUser,
    licenseType,
    companyName,
    firstLoginDate,
    lastLoginDate,
    userType,
    role,
    isTeamManager,
    isUserpilotInitialized,
  ]);

  return isUserpilotInitialized;
};

export default useUserpilot;
